import { type ChangeEvent, type FC, type ReactNode, useState } from 'react';
import { twJoin, twMerge } from '@beerntea/pitcher-ui';
import { CheckIcon } from 'lucide-react';

type TitleProps = {
  children: ReactNode;
  className?: string;
};

const Title: FC<TitleProps> = ({ children, className = '' }) => (
  <h3 className={`tw-text-[32px] tw-font-bold tw-leading-8 tw-pb-2 tw-tracking-wide tw-font-display ${className}`}>
    {children}
  </h3>
);

type TitleContactProps = {
  children: ReactNode;
  className?: string;
  variant?: string;
};

const TitleContact: FC<TitleContactProps> = ({ children, className = '', variant = 'nickel' }) => {
  const gradient = 'tw-bg-gradient-to-r tw-from-contact-from tw-to-contact-to tw-bg-clip-text tw-text-transparent';

  return (
    <h3
      className={twJoin(
        'tw-my-auto',
        'tw-font-display',
        'tw-tracking-normal tw-text-center',
        'tw-text-[80px] tw-leading-[90px]',
        'sm:tw-text-[120px] sm:tw-leading-[128px]',
        variant === 'nickel' ? 'tw-text-nickel' : 'tw-text-white',
        gradient,
        className,
      )}
    >
      {children}
    </h3>
  );
};

type TextProps = {
  children: ReactNode;
  className?: string;
};

const Text: FC<TextProps> = ({ children, className = '' }) => (
  <p className={`tw-text-base tw-font-light tw-leading-6 tw-tracking-wide tw-text-gray-700 ${className}`}>{children}</p>
);

type FieldsetTitleProps = {
  children: ReactNode;
  className?: string;
};

const FieldsetTitle: FC<FieldsetTitleProps> = ({ children, className = '' }) => (
  <h5
    className={`tw-flex tw-text-lg tw-leading-4 tw-tracking-wide tw-text-gray-500 tw-mb-2 tw-uppercase tw-font-display tw-mt-12 ${className}`}
  >
    {children}
  </h5>
);

type InputLabelProps = {
  children: ReactNode;
  className?: string;
};
const InputLabel: FC<InputLabelProps> = ({ children, className }) => (
  <div className={twMerge('tw-py-5 tw-pr-6 tw-flex-none tw-text-label', className)}>{children}</div>
);

type InputEmailProps = {
  type?: string;
  value?: string;
  name?: string;
  label?: string;
  className?: string;
  required?: boolean;
  placeholder?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
};
const Input: FC<InputEmailProps> = ({
  value = '',
  name,
  type = 'text',
  label = '',
  className = '',
  required = false,
  placeholder,
  onChange,
  onSubmit,
}) => {
  const [newValue, setNewValue] = useState<string>(value);
  const [checked, setChecked] = useState<boolean>(!!value);
  const change = (e: ChangeEvent) => {
    // @ts-expect-error unknown target
    const v = e.target.value || '';
    setNewValue(v);
    if (onChange) onChange(v);
  };
  if (type === 'textarea') {
    return (
      <label className={className}>
        {label}
        <textarea
          required={required}
          value={newValue}
          name={name}
          className="tw-uppercase tw-bg-input tw-text-body tw-placeholder-placeholder tw-border-none tw-px-6 tw-py-5 tw-h-18 tw-w-full"
          placeholder={placeholder}
          onChange={change}
          onKeyDown={(e) =>
            e.key === 'Enter'
              ? onSubmit && onSubmit(newValue)
              : (e: KeyboardEvent) => {
                  // @ts-expect-error unknown target
                  const v = e.target.value || '';
                  setNewValue(v);
                  onChange?.(v);
                }
          }
        />
      </label>
    );
  }
  if (type === 'checkbox') {
    return (
      <label className={`tw-flex tw-items-center tw-relative ${className}`}>
        <input
          type="checkbox"
          required={required}
          checked={checked}
          name={name}
          className="tw-peer tw-hidden"
          placeholder={placeholder}
          onChange={() => setChecked((prev) => !prev)}
          onKeyDown={(e) => e.key === 'Enter' && onSubmit && onSubmit(newValue)}
        />
        <div className="tw-size-10 tw-bg-input tw-grid tw-place-content-center">{checked && <CheckIcon />}</div>
        {label && <span className=" tw-px-6 tw-py-5">{label}</span>}
      </label>
    );
  }
  return (
    <label className={className}>
      {label}
      <input
        required={required}
        value={newValue}
        name={name}
        type={type}
        className="tw-uppercase tw-bg-input tw-text-body tw-placeholder-placeholder tw-border-none tw-px-6 tw-py-5 tw-w-full"
        placeholder={placeholder}
        onChange={change}
        onKeyDown={(e) => e.key === 'Enter' && onSubmit && onSubmit(newValue)}
      />
    </label>
  );
};

type InputButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
};

const InputButton: FC<InputButtonProps> = ({ children, onClick, className = '', type = 'button' }) => (
  <button
    type={type}
    className={twMerge(
      `tw-flex-none tw-flex tw-bg-button tw-text-white tw-px-6 tw-py-5 tw-uppercase tw-justify-center `,
      'tw-relative tw-overflow-hidden',
      'before:tw-transition before:tw-duration-300',
      'before:tw-absolute before:tw-block before:tw-content-[""] before:tw-top-[50%] before:tw-left-[50%] before:tw-rounded-full before:tw-origin-center before:tw-py-full',
      'before:tw-bg-white',
      'before:tw-scale-0 before:tw-w-[500px] before:tw-h-[500px]',
      'before:tw-translate-x-[-50%] before:tw-translate-y-[-50%]',
      'hover:before:tw-scale-[180%]',
      'hover:tw-text-black tw-transition tw-duration-300',
      className,
    )}
    onClick={() => onClick && onClick()}
  >
    <span className="tw-relative tw-block">
      <span className=" tw-flex">{children}</span>
    </span>
  </button>
);

export { Title, TitleContact, FieldsetTitle, Text, InputLabel, Input, InputButton };
